import {
  Button,
  HStack,
  Heading,
  IconButton,
  Input,
  Select,
  Text,
  VStack,
  Stack,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useFieldArray, useForm, Controller } from "react-hook-form";
import { FaTrashAlt } from "react-icons/fa";
import * as y from "yup";
import { ReportMode } from "src/Manager/EventDetail/Nav/Reporting/ReportsForm";
import { useState, useEffect } from "react";
import { AddIcon } from "@chakra-ui/icons";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import actions from "src/actions";

const DEFAULT_RECIPIENT = { email: "" };

const emailSchema = y.string().email().required();
const recipientSchema = y.object({ email: emailSchema });

const schema = y.object({
  recipients: y.array(recipientSchema).required(),
  report_time: y.string().optional(),
});

type FormValues = y.InferType<typeof schema>;

const defaultValues: FormValues = {
  recipients: [DEFAULT_RECIPIENT],
  report_time: "08:00",
};

const AdsReportForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [organizationTimezone, setOrganizationTimezone] =
    useState("America/New_York");

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { isDirty, isValid, errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    criteriaMode: "all",
    mode: "onChange",
    defaultValues,
  });

  const { append, fields, remove } = useFieldArray({
    control,
    name: "recipients",
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await actions.ADMIN.fetchAdsReportRecipients();
        if (cancelled) return;
        const recipients = (data?.recipients ?? []) as string[];
        reset({
          recipients:
            recipients.length > 0
              ? recipients.map((r) => ({ email: r }))
              : [DEFAULT_RECIPIENT],
          report_time: data?.report_time ?? "08:00",
        });
      } catch {
        if (!cancelled) reset(defaultValues);
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reset]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const organization = await actions.ADMIN.fetchOrganization();
        if (cancelled) return;
        setOrganizationTimezone(
          organization?.settings?.timezone ?? "America/New_York"
        );
      } catch {
        if (cancelled) return;
        setOrganizationTimezone("America/New_York");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true);
    try {
      await actions.ADMIN.createAdsReportRecipients({
        mode: ReportMode.WEEKLY,
        recipients: values.recipients.map((r) => r.email),
        report_time: values.report_time ?? "08:00",
      });
      reset(values);
    } finally {
      setIsLoading(false);
    }
  };

  if (initialLoading) {
    return null;
  }

  return (
    <VStack w="full" my={4} alignItems="flex-start" gap={6}>
      <Heading size="sm" py={2}>
        Meta Ads Report Emails
      </Heading>
      <Text fontSize="sm" color="gray.600">
        Weekly email with campaign performance and optimization suggestions.
      </Text>

      <VStack w="full" alignItems="flex-start" spacing={0}>
        <Text fontWeight="medium" marginBottom={2}>
          Report time
        </Text>
        <Controller
          control={control}
          name="report_time"
          render={({ field }) => (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                label="Time"
                value={
                  field.value
                    ? dayjs(field.value, "HH:mm")
                    : dayjs("08:00", "HH:mm")
                }
                onChange={(newValue) =>
                  field.onChange(newValue ? newValue.format("HH:mm") : "08:00")
                }
                disabled={isLoading}
                views={["hours", "minutes"]}
                ampm
              />
            </LocalizationProvider>
          )}
        />
        <Text fontSize="sm" color="gray.600" marginTop={2}>
          Time is in the {organizationTimezone} timezone.
        </Text>
      </VStack>

      <Stack w="full" spacing={0}>
        <Text fontWeight="medium">Send reports to</Text>
        <Stack w="full" spacing={2} mt={2}>
          {fields.map((field, index) => (
            <HStack w="full" justifyContent="space-between" key={field.id}>
              <Input
                isDisabled={isLoading}
                isInvalid={!!errors.recipients?.[index]?.email}
                {...register(`recipients.${index}.email`)}
                placeholder="email@example.com"
              />
              <IconButton
                isDisabled={isLoading}
                aria-label="Remove recipient"
                variant="ghost"
                onClick={() => remove(index)}
                icon={<FaTrashAlt fontSize="1rem" />}
              />
            </HStack>
          ))}
        </Stack>
      </Stack>
      <Button
        leftIcon={<AddIcon />}
        size="sm"
        onClick={() => append(DEFAULT_RECIPIENT)}
      >
        Add recipient
      </Button>

      <HStack>
        <Button
          isDisabled={!isDirty || !isValid || isLoading}
          isLoading={isLoading}
          colorScheme="green"
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </Button>
        <Button
          variant="ghost"
          onClick={() => reset()}
          isDisabled={!isDirty || isLoading}
        >
          Cancel
        </Button>
      </HStack>
    </VStack>
  );
};

export default AdsReportForm;
